import React, { useState, useEffect } from "react";
import { Plus, Save } from "lucide-react";
import { resultsApi, clientsApi } from "../../utils/api";
import SearchBar from "../common/SearchBar";
import FormModal from "../common/FormModal";
import ActionButtons, {
  PrimaryButton,
  SecondaryButton,
} from "../common/ActionButtons";

const ResultManagement = () => {
  const [results, setResults] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingResult, setEditingResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    customerId: 0,
    dataIndicatorId: 0,
    value: "",
    description: "",
    loadDateTime: new Date().toISOString(),
    clientId: "",
  });

  useEffect(() => {
    fetchResults();
    fetchClients();
  }, []);

  const fetchResults = async () => {
    setLoading(true);
    try {
      const data = await resultsApi.getAll();
      setResults(data);
    } catch (error) {
      console.error("Error fetching results:", error);
    }
    setLoading(false);
  };

  const fetchClients = async () => {
    try {
      const data = await clientsApi.getAll();
      setClients(data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingResult) {
        await resultsApi.update(editingResult.id, formData);
      } else {
        await resultsApi.create(formData);
      }
      fetchResults();
      resetForm();
    } catch (error) {
      console.error("Error saving result:", error);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this result?")) return;
    setLoading(true);
    try {
      await resultsApi.delete(id);
      fetchResults();
    } catch (error) {
      console.error("Error deleting result:", error);
    }
    setLoading(false);
  };

  const handleEdit = (result) => {
    setEditingResult(result);
    setFormData({
      customerId: result.customerid || 0,
      dataIndicatorId: result.dataindicatorid || 0,
      value: result.value || "",
      description: result.description || "",
      loadDateTime: result.loaddatetime || new Date().toISOString(),
      clientId: result.clientid || "",
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      customerId: 0,
      dataIndicatorId: 0,
      value: "",
      description: "",
      loadDateTime: new Date().toISOString(),
      clientId: "",
    });
    setEditingResult(null);
    setShowForm(false);
  };

  const filteredResults = results.filter(
    (result) =>
      result.value?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.description?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          onRefresh={fetchResults}
          placeholder="Search results..."
        />
        <PrimaryButton onClick={() => setShowForm(!showForm)} icon={Plus}>
          New Result
        </PrimaryButton>
      </div>

      <FormModal
        title={editingResult ? "Edit Result" : "New Result"}
        isOpen={showForm}
        onClose={resetForm}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Value *
              </label>
              <input
                type="text"
                required
                value={formData.value}
                onChange={(e) =>
                  setFormData({ ...formData, value: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Load DateTime
              </label>
              <input
                type="datetime-local"
                value={formData.loadDateTime.slice(0, 16)}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    loadDateTime: new Date(e.target.value).toISOString(),
                  })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Customer ID
              </label>
              <input
                type="number"
                value={formData.customerId}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    customerId: parseInt(e.target.value) || 0,
                  })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Indicator ID
              </label>
              <input
                type="number"
                value={formData.dataIndicatorId}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    dataIndicatorId: parseInt(e.target.value) || 0,
                  })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Client
              </label>
              <select
                value={formData.clientId}
                onChange={(e) =>
                  setFormData({ ...formData, clientId: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select client (optional)</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <SecondaryButton onClick={resetForm}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" disabled={loading} icon={Save}>
              {loading ? "Saving..." : "Save Result"}
            </PrimaryButton>
          </div>
        </form>
      </FormModal>

      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Value
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Load DateTime
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Indicator ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  Loading...
                </td>
              </tr>
            ) : filteredResults.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  No results found
                </td>
              </tr>
            ) : (
              filteredResults.map((result) => (
                <tr key={result.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                    {result.value || "N/A"}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {result.description || "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    {result.loaddatetime
                      ? new Date(result.loaddatetime).toLocaleString()
                      : "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    {result.dataindicatorid || "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    <ActionButtons
                      onEdit={() => handleEdit(result)}
                      onDelete={() => handleDelete(result.id)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ResultManagement;
